import React, { useRef, useState } from "react";
import Papa from "papaparse";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, CheckCircle2, AlertTriangle, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { IMPORT_SOURCES, HEADER_MAP, isTruthy } from "@/lib/importSources";

const STATUS_PILL = {
  new: ["New", "text-[color:var(--vault-success)] bg-[color:var(--vault-success-bg)]"],
  update: ["Updates password", "text-[color:var(--vault-warning)] bg-[color:var(--vault-warning-bg)]"],
  duplicate: ["Duplicate", "text-[color:var(--vault-text-tertiary)] bg-[color:var(--vault-input-bg)]"],
};

function buildColumnIndex(headers) {
  const norm = headers.map((h) => (h || "").trim().toLowerCase());
  const idx = {};
  for (const [field, variants] of Object.entries(HEADER_MAP)) {
    const found = norm.findIndex((h) => variants.includes(h));
    if (found !== -1) idx[field] = headers[found];
  }
  return idx;
}

function rowsToItems(rows, colIndex) {
  const get = (row, field) => {
    const key = colIndex[field];
    return key ? String(row[key] ?? "").trim() : "";
  };
  return rows
    .map((row) => {
      let url = get(row, "url");
      const kind = get(row, "kind").toLowerCase();
      const isNote = kind === "note" || kind === "securenote" || url === "http://sn";
      if (url === "http://sn") url = "";
      const title = get(row, "title") || url || get(row, "username");
      if (!title) return null;
      const tagRaw = get(row, "tags");
      const tags = tagRaw
        ? tagRaw.split(/[;,|]/).map((t) => t.trim()).filter(Boolean).slice(0, 10)
        : [];
      return {
        type: isNote ? "note" : "login",
        title: title.slice(0, 200),
        username: get(row, "username"),
        url,
        password: get(row, "password"),
        notes: get(row, "notes"),
        category: "Personal",
        tags,
        is_favorite: isTruthy(get(row, "favorite")),
      };
    })
    .filter(Boolean);
}

export default function ImportDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [items, setItems] = useState([]);
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [checking, setChecking] = useState(false);
  const [source, setSource] = useState("1password");
  const src = IMPORT_SOURCES.find((x) => x.id === source);

  const reset = () => {
    setFileName("");
    setItems([]);
    setParseError("");
    setSummary(null);
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleFile = (file) => {
    if (!file) return;
    setParseError("");
    setItems([]);
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const headers = res.meta?.fields || [];
        const colIndex = buildColumnIndex(headers);
        if (!colIndex.title && !colIndex.username && !colIndex.url) {
          setParseError("We couldn't find recognizable columns (like Title, Username, Password) in this file. Make sure it's a CSV exported from your password manager.");
          return;
        }
        const parsed = rowsToItems(res.data, colIndex);
        if (parsed.length === 0) {
          setParseError("No importable rows were found in this file.");
          return;
        }
        setItems(parsed);
        setChecking(true);
        api.post("/vault/import", { items: parsed, dry_run: true })
          .then(({ data }) => setSummary(data))
          .catch(() => setSummary(null))
          .finally(() => setChecking(false));
      },
      error: () => setParseError("Could not read that file. Please upload a valid .csv file."),
    });
  };

  const onInputChange = (e) => handleFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const doImport = async () => {
    if (items.length === 0) return;
    setImporting(true);
    try {
      const { data } = await api.post("/vault/import", { items });
      const parts = [`${data.imported} added`];
      if (data.updated) parts.push(`${data.updated} password${data.updated === 1 ? "" : "s"} updated`);
      if (data.skipped) parts.push(`${data.skipped} duplicate${data.skipped === 1 ? "" : "s"} skipped`);
      toast.success(`Import complete: ${parts.join(", ")}`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white rounded-2xl [&>*]:min-w-0" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl">Import passwords</DialogTitle>
          <DialogDescription>Bring your logins over from another password manager. Duplicates are detected automatically.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-1 p-1 rounded-xl bg-[color:var(--vault-input-bg)]" data-testid="import-source-tabs">
          {IMPORT_SOURCES.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => setSource(x.id)}
              className={`text-xs sm:text-sm font-medium rounded-lg py-1.5 transition-colors ${source === x.id ? "bg-[color:var(--vault-card-bg)] text-[color:var(--vault-brand)] shadow-sm" : "text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-text-primary)]"}`}
              data-testid={`import-source-${x.id}`}
            >
              {x.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-[color:var(--vault-text-secondary)] -mt-1" data-testid="import-source-steps">
          {src.steps} Then upload that .csv file here.
        </p>

        {items.length === 0 ? (
          <>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="border-2 border-dashed border-[color:var(--vault-border)] rounded-2xl p-8 text-center cursor-pointer hover:border-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)] transition-colors"
              data-testid="import-dropzone"
            >
              <div className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center text-white" style={{ background: "var(--vault-gradient)" }}>
                <UploadCloud size={22} />
              </div>
              <p className="font-[Outfit] font-medium mt-3">Drop your CSV here, or click to browse</p>
              <p className="text-sm text-[color:var(--vault-text-tertiary)] mt-1 break-all">{fileName || `Accepts .csv exported from ${src.label}`}</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onInputChange}
                className="hidden"
                data-testid="import-file-input"
              />
            </div>
            {parseError && (
              <div className="flex items-start gap-2 text-sm text-[color:var(--vault-error)] bg-[color:var(--vault-error-bg)] rounded-xl p-3" data-testid="import-error">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{parseError}</span>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3 min-w-0" data-testid="import-preview">
            <div className="flex items-center gap-2 text-sm bg-[color:var(--vault-brand-light)] text-[color:var(--vault-brand)] rounded-xl p-3">
              <CheckCircle2 size={16} className="shrink-0" />
              <span className="min-w-0 break-words"><span className="font-semibold">{items.length}</span> item{items.length === 1 ? "" : "s"} ready to import from <span className="font-medium break-all">{fileName}</span></span>
            </div>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-[color:var(--vault-border)] divide-y divide-[color:var(--vault-border)]">
              {items.slice(0, 50).map((it, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2" data-testid={`import-row-${i}`}>
                  <FileText size={15} className="text-[color:var(--vault-text-tertiary)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.title}</div>
                    <div className="text-xs text-[color:var(--vault-text-tertiary)] truncate">{it.username || it.url || "—"}</div>
                  </div>
                  {summary?.statuses?.[i] && (
                    <span className={`pill shrink-0 !text-[10px] !px-2 !py-0.5 ${STATUS_PILL[summary.statuses[i]][1]}`} data-testid={`import-row-status-${i}`}>
                      {STATUS_PILL[summary.statuses[i]][0]}
                    </span>
                  )}
                  <span className="shrink-0 w-12 text-right text-xs font-mono text-[color:var(--vault-text-tertiary)]">{it.password ? "••••" : "no pw"}</span>
                </div>
              ))}
              {items.length > 50 && (
                <div className="px-3 py-2 text-xs text-[color:var(--vault-text-tertiary)] text-center">…and {items.length - 50} more</div>
              )}
            </div>
            {summary && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="import-summary">
                {[
                  ["New", summary.imported, "import-summary-new"],
                  ["Password updates", summary.updated, "import-summary-updated"],
                  ["Duplicates skipped", summary.skipped, "import-summary-skipped"],
                ].map(([label, n, tid]) => (
                  <div key={tid} className="rounded-xl border border-[color:var(--vault-border)] p-2" data-testid={tid}>
                    <div className="font-[Outfit] text-lg font-semibold">{n}</div>
                    <div className="text-[11px] text-[color:var(--vault-text-tertiary)] leading-tight">{label}</div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={reset}
              className="text-sm text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] inline-flex items-center gap-1"
              data-testid="import-choose-different"
            >
              <X size={14} /> Choose a different file
            </button>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => handleClose(false)} data-testid="import-cancel-btn">
            Cancel
          </Button>
          <Button
            type="button"
            disabled={items.length === 0 || importing || checking || (summary && summary.imported + summary.updated === 0)}
            onClick={doImport}
            className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white"
            data-testid="import-confirm-btn"
          >
            {importing
              ? "Importing…"
              : checking
                ? "Checking for duplicates…"
              : summary
                ? summary.imported + summary.updated === 0 ? "Nothing new to import" : `Import ${summary.imported + summary.updated} change${summary.imported + summary.updated === 1 ? "" : "s"}`
                : items.length > 0 ? `Import ${items.length} item${items.length === 1 ? "" : "s"}` : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
